import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { alpha } from "@/globals/theme";
import { ArrowCounterClockwiseIcon, DownloadIcon, DownloadSimpleIcon, PackageIcon, TrendUpIcon } from "@phosphor-icons/react";

const schema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    src: ["http", "https"],
  },
  tagNames: [...(defaultSchema.tagNames || []), "img"],
  attributes: {
    ...defaultSchema.attributes,
    img: [
      ...(defaultSchema.attributes?.img || []),
      "src",
      "alt",
      "title",
      "width",
      "height",
    ],
  },
};

// Types
interface RepoConfig {
  owner: string;
  repo: string;
}

type RepoInput = string | RepoConfig;

interface Asset {
  id: number;
  name: string;
  download_count: number;
  size: number;
  browser_download_url: string;
}

interface Release {
  id: number;
  tag_name: string;
  name: string | null;
  published_at: string;
  assets: Asset[];
  body: string | null; // <-- add this
}

interface GithubDownloadTrackerProps {
  repo: RepoInput;
  className?: string;
}

// Styled Components
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.secondaryBackground};
  padding: 2rem;
  border-radius: 16px;
  margin: 15px 0px;
`;

const MaxWidthWrapper = styled.div`
  max-width: 72rem;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const BaseStatCard = styled.div`
border: 1px solid transparent;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`;

const StatCardDownloads = styled(BaseStatCard)`
  background:  ${({ theme }) => alpha(theme.colors.success, 0.12)};
    border-color:  ${({ theme }) => theme.colors.success};
`

const StatCardReleases = styled(BaseStatCard)`
  background:  ${({ theme }) => alpha(theme.colors.info, 0.12)};
  border-color:  ${({ theme }) => theme.colors.info};
`

const StatCardLatestVersion = styled(BaseStatCard)`
  background:  ${({ theme }) => alpha(theme.colors.primary, 0.12)};
  border-color:  ${({ theme }) => theme.colors.primary};
`

const StatCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatInfo = styled.div``;

const StatLabel = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  opacity: 0.9;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StatValue = styled.p`
  color: ${({ theme }) => theme.colors.primaryBackground};
  font-size: 1.875rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StatValueSmall = styled(StatValue)`
  font-size: 1.5rem;
`;

const ReleasesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReleaseCard = styled.div`
  background-color: ${({ theme }) => theme.colors.primaryBackground};
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 5px 7px rgba(0, 0, 0, 0.3);
  border: 1px solid ${({ theme }) => theme.colors.primaryBackground};
`;

const ReleaseHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ReleaseInfo = styled.div`
display: flex;
flex-direction: column;
  align-items: flex-start;

`;

const ReleaseName = styled.h1`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.25rem;
`;

const ReleaseDate = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ReleaseStats = styled.div`
  text-align: right;
`;

const ReleaseStatsLabel = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ReleaseStatsValue = styled.p`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`;

const ReleaseBody = styled.div`
  margin-bottom: 1rem;
  user-select: text;

  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.typography.lineHeights.body};
  text-align: left;

  
  /* Override the global img reset (display:block/width:100%) so badges
     render at their natural size and flow inline like they do on
     npm/GitHub, instead of stretching full-width and stacking. */
  img {
    display: inline-block;
    width: auto;
    max-width: 100%;
    height: auto;
    vertical-align: middle;
  }

  a:has(img) {
    display: inline-flex;
    align-items: center;
  }

  /* Paragraphs that are just a row of badges (![]() shields) should wrap
     as a tight flex row instead of the normal paragraph block spacing,
     which is what was causing each badge to render on its own line. */
  p:has(img) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 0 0 10px;
  }

  h1,
  h2,
  h3,
  h4 {
    font-family: ${({ theme }) => theme.typography.fonts.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
    margin: 1.4em 0 0.5em;
    line-height: ${({ theme }) => theme.typography.lineHeights.heading};

    &:first-child {
      margin-top: 0;
    }
  }

  h2 {
    font-size: ${({ theme }) => theme.typography.sizes.title};
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.sizes.subject};
  }

  p {
    margin: 0 0 1em;
  }

  ul,
  ol {
    margin: 0 0 1em;
    padding-left: 1.4em;
  }

  li {
    margin-bottom: 0.3em;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  code {
    font-family: ${({ theme }) => theme.typography.fonts.mono};
    font-size: 0.9em;
    background: ${({ theme }) => theme.colors.secondaryBackground};
    padding: 1px 5px;
    border-radius: ${({ theme }) => theme.borders.radius.small};
  }

  pre {
    background: ${({ theme }) => theme.colors.secondaryBackground};
    border-radius: ${({ theme }) => theme.borders.radius.medium};
    padding: 12px 14px;
    overflow-x: auto;
    margin: 0 0 1em;

    code {
      background: none;
      padding: 0;
      font-size: ${({ theme }) => theme.typography.sizes.hint};
    }
  }

  hr {
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
    margin: 1.5em 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0 0 1em;
    font-size: ${({ theme }) => theme.typography.sizes.hint};
  }

  th,
  td {
    border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
    padding: 6px 10px;
    text-align: left;
  }

  th {
    font-family: ${({ theme }) => theme.typography.fonts.semibold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;




const AssetsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AssetItem = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: 0.5rem;
  padding: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
      svg {
      color: ${({ theme }) => theme.colors.textPrimary};
    
    }
  }
`;

const AssetInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;


`;

const AssetDetails = styled.div``;

const AssetName = styled.p`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
`;

const AssetSize = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const AssetDownloads = styled.div`
  text-align: right;
`;

const AssetDownloadCount = styled.p`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 600;
`;

const AssetDownloadLabel = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const RefreshButtonContainer = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryBackground};
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryBackground};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.secondaryBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingContent = styled.div`
  text-align: center;
`;

const SpinningIcon = styled(ArrowCounterClockwiseIcon)`
  width: 3rem;
  height: 3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 auto 1rem;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1.125rem;
`;

const ErrorContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom right, #111827, #1f2937);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ErrorCard = styled.div`
  background-color: rgba(127, 29, 29, 0.2);
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 0.5rem;
  padding: 1.5rem;
  max-width: 28rem;
`;

const ErrorTitle = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ErrorMessage = styled.p`
  color: #d1d5db;
`;

const ErrorButton = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const WarningCard = styled(ErrorCard)`
  background-color: rgba(113, 63, 18, 0.2);
  border-color: #eab308;
`;

const WarningTitle = styled(ErrorTitle)`
  color: #fbbf24;
`;

// Utility function to parse repo input
const parseRepoInput = (input: RepoInput): RepoConfig | null => {
  if (typeof input === "string") {
    const patterns = [
      /github\.com\/([^\/]+)\/([^\/\?#]+)/,
      /github\.com\/([^\/]+)\/([^\/]+)\/releases/,
      /github\.com\/([^\/]+)\/([^\/]+)\/releases\/download/,
      /^([^\/]+)\/([^\/]+)$/,
    ];

    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match) {
        return {
          owner: match[1],
          repo: match[2],
        };
      }
    }

    console.error("Invalid GitHub repository format:", input);
    return null;
  }

  if (input.owner && input.repo) {
    return input;
  }

  console.error("Invalid repository configuration:", input);
  return null;
};

const GithubDownloadTracker: React.FC<GithubDownloadTrackerProps> = ({
  repo,
  className = "",
}) => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalDownloads, setTotalDownloads] = useState(0);
  const [repoConfig, setRepoConfig] = useState<RepoConfig | null>(null);

  useEffect(() => {
    const config = parseRepoInput(repo);
    setRepoConfig(config);

    if (!config) {
      setError("Invalid repository configuration");
      setLoading(false);
    }
  }, [repo]);

  const fetchReleaseData = async () => {
    if (!repoConfig) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://api.github.com/repos/${repoConfig.owner}/${repoConfig.repo}/releases`,
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch release data: ${response.status} ${response.statusText}`,
        );
      }

      const data: Release[] = await response.json();

      // Sort by release date descending (most recent first)
      data.sort(
        (a, b) =>
          new Date(b.published_at).getTime() -
          new Date(a.published_at).getTime(),
      );

      // Take only top 5 most recent releases
      const topReleases = data.slice(0, 5);

      setReleases(topReleases);

      const total = data.reduce((sum, release) => {
        const releaseTotal = release.assets.reduce(
          (assetSum, asset) => assetSum + asset.download_count,
          0,
        );
        return sum + releaseTotal;
      }, 0);

      setTotalDownloads(total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (repoConfig) {
      fetchReleaseData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoConfig]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatFileSize = (bytes: number): string => {
    return (bytes / 1024 / 1024).toFixed(2);
  };

  if (loading) {
    return (
      <LoadingContainer className={className}>
        <LoadingContent>
          <SpinningIcon />
          <LoadingText>Loading release data...</LoadingText>
        </LoadingContent>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer className={className}>
        <ErrorCard>
          <ErrorTitle>Error Loading Data</ErrorTitle>
          <ErrorMessage>{error}</ErrorMessage>
          {repoConfig && (
            <ErrorButton onClick={fetchReleaseData}>Try Again</ErrorButton>
          )}
        </ErrorCard>
      </ErrorContainer>
    );
  }

  if (!repoConfig) {
    return (
      <ErrorContainer className={className}>
        <WarningCard>
          <WarningTitle>Invalid Configuration</WarningTitle>
          <ErrorMessage>Please provide a valid GitHub repository.</ErrorMessage>
        </WarningCard>
      </ErrorContainer>
    );
  }

  return (
    <Container className={className}>
      <MaxWidthWrapper>
        <Header>
          <Title>Downloads</Title>
          <Subtitle>
            Track download statistics for {repoConfig.owner}/{repoConfig.repo}
          </Subtitle>
        </Header>

        <StatsGrid>
          <StatCardDownloads >
            <StatCardContent>
              <StatInfo>
                <StatLabel>Total Downloads</StatLabel>
                <StatValue>{formatNumber(totalDownloads)}</StatValue>
              </StatInfo>
              <DownloadIcon size={48} />
            </StatCardContent>
          </StatCardDownloads>

          <StatCardReleases>
            <StatCardContent>
              <StatInfo>
                <StatLabel>Total Releases</StatLabel>
                <StatValue>{releases.length}</StatValue>
              </StatInfo>
              <PackageIcon size={48} />
            </StatCardContent>
          </StatCardReleases>

          <StatCardLatestVersion>
            <StatCardContent>
              <StatInfo>
                <StatLabel>Latest Version</StatLabel>
                <StatValueSmall>
                  {releases[0]?.tag_name || "N/A"}
                </StatValueSmall>
              </StatInfo>
              <TrendUpIcon size={48} />
            </StatCardContent>
          </StatCardLatestVersion>
        </StatsGrid>

        <ReleasesList>
          {releases.map((release) => {
            const releaseTotal = release.assets.reduce(
              (sum, asset) => sum + asset.download_count,
              0,
            );

            return (
              <ReleaseCard key={release.id}>
                <ReleaseHeader>
                  <ReleaseInfo>
                    <ReleaseName>
                      {release.name || release.tag_name}
                    </ReleaseName>
                    <ReleaseDate>
                      Released on {formatDate(release.published_at)}
                    </ReleaseDate>
                  </ReleaseInfo>
                  <ReleaseStats>
                    <ReleaseStatsLabel>Total Downloads</ReleaseStatsLabel>
                    <ReleaseStatsValue>
                      {formatNumber(releaseTotal)}
                    </ReleaseStatsValue>
                  </ReleaseStats>
                </ReleaseHeader>
                <ReleaseBody>
                  {release.body &&
                    release.name !== "Majik Runway Desktop v1.0.1" ? (
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw, [rehypeSanitize, schema]]}
                      remarkPlugins={[remarkGfm]}
                    >
                      {release.body}
                    </ReactMarkdown>
                  ) : (
                    "No description provided."
                  )}
                </ReleaseBody>

                <AssetsList>
                  {release.assets
                    .filter(
                      (asset) =>
                        asset.name.endsWith(".deb") ||
                        asset.name.endsWith(".rpm") ||
                        asset.name.endsWith(".snap") ||
                        asset.name.endsWith(".AppImage") ||
                        asset.name.endsWith(".exe") ||
                        asset.name.endsWith(".msix") ||
                        asset.name.endsWith(".msi"),
                    )
                    .map((asset) => (
                      <AssetItem
                        key={asset.id}
                        download
                        href={asset.browser_download_url}
                      >
                        <AssetInfo>
                          <DownloadSimpleIcon size={20} />
                          <AssetDetails>
                            <AssetName>{asset.name}</AssetName>
                            <AssetSize>
                              {formatFileSize(asset.size)} MB
                            </AssetSize>
                          </AssetDetails>
                        </AssetInfo>
                        <AssetDownloads>
                          <AssetDownloadCount>
                            {formatNumber(asset.download_count)}
                          </AssetDownloadCount>
                          <AssetDownloadLabel>downloads</AssetDownloadLabel>
                        </AssetDownloads>
                      </AssetItem>
                    ))}
                </AssetsList>
              </ReleaseCard>
            );
          })}
        </ReleasesList>

        <RefreshButtonContainer>
          <RefreshButton onClick={fetchReleaseData}>
            <ArrowCounterClockwiseIcon size={20} />
            <span>Refresh Data</span>
          </RefreshButton>
        </RefreshButtonContainer>
      </MaxWidthWrapper>
    </Container>
  );
};

export default GithubDownloadTracker;
